import React, { useState, useEffect } from 'react';
import { UserPlus, Send, Copy, Check, Users, ChevronLeft, ChevronRight, UserX } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';

const SHARE_MSG = (link) => `I just took this personality test and it's wild how accurate it is ✨ Take it so we can compare — are we better as friends, lovers, or startup partners? ${link}`;

export const SharePanel = ({ link }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <div className="share-panel-actions">
            <a href={`https://wa.me/?text=${encodeURIComponent(SHARE_MSG(link))}`} target="_blank" rel="noopener noreferrer"
                className="share-panel-btn whatsapp">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                WhatsApp
            </a>
            <a href={`sms:?body=${encodeURIComponent(SHARE_MSG(link))}`}
                className="share-panel-btn sms">
                💬 SMS
            </a>
            <button onClick={handleCopy}
                className={`share-panel-btn copy ${copied ? 'copied' : ''}`}>
                {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Link</>}
            </button>
        </div>
    );
};

const SyncTab = ({ user, analysisResult, onRestart, onCompareRequested, isActive = true }) => {
    const [inviteLink, setInviteLink] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [connections, setConnections] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [selectedConnection, setSelectedConnection] = useState(null);
    const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
    
    // States from HEAD (Backend Compatibility)
    const [selectedCategory, setSelectedCategory] = useState("Overall");
    const [compatibilityReports, setCompatibilityReports] = useState([]);

    // States from main (UI Pagination)
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [profilePage, setProfilePage] = useState(1);
    const [leaderboardPage, setLeaderboardPage] = useState(1);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // Reset pages when limit or category changes
    useEffect(() => { setProfilePage(1); }, [itemsPerPage]);
    useEffect(() => { setLeaderboardPage(1); }, [itemsPerPage, selectedCategory]);

    const fetchLeaderboardData = React.useCallback(async () => {
        if (!user?.id) return;
        try {
            const t = new Date().getTime();
            const response = await fetch(`/api/compatibility/user/${user.id}?t=${t}`, { cache: 'no-store' });
            const data = await response.json();
            if (data.success) {
                setCompatibilityReports(data.reports || []);
            }
        } catch (err) {
            console.error('Failed to fetch leaderboard data:', err);
        }
    }, [user?.id]);

    useEffect(() => {
        if (!isActive) return;
        if (user?.id && clerkLoaded) {
            setIsInitialLoading(true); // Force loading state on every tab open
            const clerkId = clerkUser?.id;
            const baseUrl = clerkId && clerkId !== user.id
                ? `/api/invites/user/${user.id}?clerk_id=${clerkId}`
                : `/api/invites/user/${user.id}`;
            const url = baseUrl + (baseUrl.includes('?') ? '&' : '?') + `t=${new Date().getTime()}`;
            
            fetch(url, { cache: 'no-store' })
                .then(res => res.json())
                .then(data => {
                    if (data.success && data.invites) {
                        const seen = new Set();
                        const accepted = data.invites.filter(i => {
                            const isLinked = i.status === 'accepted' || i.status === 'completed';
                            if (isLinked && !seen.has(i.id)) {
                                seen.add(i.id);
                                return true;
                            }
                            return false;
                        });
                        setConnections(accepted);

                        const pending = data.invites.filter(i => 
                            i.status === 'pending' && 
                            i._perspective === 'received' && 
                            !seen.has(i.id)
                        );
                        setPendingRequests(pending);
                    }
                })
                .catch(err => console.error(err))
                .finally(() => setIsInitialLoading(false));
            
            fetchLeaderboardData();
        }
    }, [user?.id, clerkUser?.id, clerkLoaded, fetchLeaderboardData, isActive]);

    const handleGenerateLink = async () => {
        setIsLoading(true);
        try {
            if (user?.id) {
                const activeRes = await fetch(`/api/invites/active/${user.id}`);
                const activeData = await activeRes.json();
                if (activeData.success && activeData.invite_link) {
                    setInviteLink(activeData.invite_link);
                    setIsLoading(false);
                    return;
                }
            }
            const response = await fetch('/api/invites/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    inviter_user_id: user?.id,
                    inviter_test_results: analysisResult || {}
                })
            });
            const data = await response.json();
            if (data.success) {
                setInviteLink(data.invite_link);
            } else {
                console.error(data.error || 'Failed to generate invite link');
            }
        } catch {
            console.error('Failed to generate invite link');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (user?.id && clerkLoaded) {
            // Auto fetch or generate universal share link
            if (!inviteLink) handleGenerateLink();

            // Pass Clerk ID as a fallback: invites created during a failed-backend session
            // store the Clerk ID as inviter_user_id. We now query with both IDs.
            const clerkId = clerkUser?.id;
            const url = clerkId && clerkId !== user.id
                ? `/api/invites/user/${user.id}?clerk_id=${clerkId}`
                : `/api/invites/user/${user.id}`;
            fetch(url)
                .then(res => res.json())
                .then(data => {
                    if (data.success && data.invites) {
                        const seen = new Set();
                        
                        // 1. Linked: Accepted or Completed
                        const accepted = data.invites.filter(i => {
                            const isLinked = i.status === 'accepted' || i.status === 'completed';
                            if (isLinked && !seen.has(i.id)) {
                                seen.add(i.id);
                                return true;
                            }
                            return false;
                        });
                        setConnections(accepted);
 
                        // 2. Pending: Received but not yet accepted
                        const pending = data.invites.filter(i => 
                            i.status === 'pending' && 
                            i._perspective === 'received' && 
                            !seen.has(i.id)
                        );
                        setPendingRequests(pending);
                    }
                })
                .catch(err => console.error(err))
                .finally(() => setIsInitialLoading(false));
        }
    }, [user?.id, clerkUser?.id, clerkLoaded]);

    const getDisplayName = (inv) => {
        if (inv._connected_name) return inv._connected_name;
        if (inv.invited_email && !['link-invite', 'link-invite-accepted'].includes(inv.invited_email)) {
            return inv.invited_email;
        }
        return inv._perspective === 'received' ? 'Synced Friend' : 'Linked Profile';
    };

    const getAvatar = (inv) => {
        const name = getDisplayName(inv);
        return /^[A-Za-z]/.test(name) ? name.charAt(0).toUpperCase() : '🔗';
    };

    const getConnectedPersona = (inv) => {
        try {
            const raw = inv._perspective === 'received'
                ? inv.inviter_test_results
                : inv.invited_test_results;
            if (!raw) return null;
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            const pCard = parsed?.cards_data?.personality_card || parsed?.personality_card || {};
            return pCard?.persona || parsed?.persona || parsed?.dominance || null;
        } catch { return null; }
    };
 
    const PaginationControls = ({ currentPage, totalItems, onPageChange }) => {
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        if (totalPages <= 1) return null;

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginTop: '20px', padding: '10px 0' }}>
                <button 
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                    style={{ 
                        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px', padding: '6px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        color: currentPage === 1 ? 'rgba(255,255,255,0.1)' : 'white', display: 'flex'
                    }}
                >
                    <ChevronLeft size={18} />
                </button>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', fontWeight: '600', letterSpacing: '0.5px' }}>
                    PAGE {currentPage} OF {totalPages}
                </span>
                <button 
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                    style={{ 
                        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px', padding: '6px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        color: currentPage === totalPages ? 'rgba(255,255,255,0.1)' : 'white', display: 'flex'
                    }}
                >
                    <ChevronRight size={18} />
                </button>
            </div>
        );
    };

    const LimitSelector = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase' }}>Show</span>
            <select 
                value={itemsPerPage} 
                onChange={e => setItemsPerPage(Number(e.target.value))}
                style={{
                    background: 'rgba(0,0,0,0.3)', color: 'white', border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '6px', padding: '4px 8px', fontSize: '11px', outline: 'none', cursor: 'pointer'
                }}
            >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
            </select>
        </div>
    );
 
    const ConnectionSkeleton = () => (
        <div className="sync-list">
            {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton-item" style={{
                    padding: '16px', borderRadius: '16px', background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.05)', marginBottom: '10px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    position: 'relative', overflow: 'hidden'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                        <div>
                            <div style={{ width: '120px', height: '14px', background: 'rgba(255,255,255,0.08)', borderRadius: '4px', marginBottom: '8px' }} />
                            <div style={{ width: '70px', height: '10px', background: 'rgba(255,255,255,0.04)', borderRadius: '3px' }} />
                        </div>
                    </div>
                    {/* Shimmer Overlay */}
                    <div className="shimmer-overlay" />
                </div>
            ))}
            <style>{`
                .skeleton-item {
                    isolation: isolate;
                }
                .shimmer-overlay {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(
                        90deg, 
                        rgba(255, 255, 255, 0) 0%, 
                        rgba(255, 255, 255, 0.05) 50%, 
                        rgba(255, 255, 255, 0) 100%
                    );
                    animation: shimmer 1.8s infinite;
                    transform: translateX(-100%);
                }
                @keyframes shimmer {
                    100% { transform: translateX(100%); }
                }
            `}</style>
        </div>
    );
 
    return (
        <div>

            {pendingRequests.length > 0 && (
                <div className="glass-panel" style={{ marginBottom: '24px', border: '1px solid rgba(236,72,153,0.3)', background: 'rgba(236,72,153,0.05)' }}>
                    <h3 className="section-title" style={{ color: '#f472b6', marginBottom: '12px' }}>
                        📬 Pending Invitations
                    </h3>
                    <div className="sync-list">
                        {pendingRequests.map((inv, idx) => (
                            <div key={inv.id || idx} style={{
                                padding: '12px', borderRadius: '12px', background: 'rgba(0,0,0,0.2)',
                                border: '1px solid rgba(255,255,255,0.05)', marginBottom: '8px',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div className="sync-avatar" style={{ width: '32px', height: '32px', fontSize: '12px', overflow: 'hidden' }}>
                                        {inv._connected_image_url ? (
                                            <img src={inv._connected_image_url} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            getAvatar(inv)
                                        )}
                                    </div>
                                    <div>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'white' }}>{getDisplayName(inv)}</div>
                                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Sent you a request</div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => onRestart()}
                                    style={{
                                        background: 'rgba(236,72,153,0.2)', color: '#f472b6', border: '1px solid rgba(236,72,153,0.3)',
                                        borderRadius: '8px', padding: '6px 12px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'
                                    }}
                                >
                                    View & Compare
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="glass-panel">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <h3 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
                        <Users size={16} color="#ec4899" /> Your Connections
                    </h3>
                    <LimitSelector />
                </div>
                {isInitialLoading ? (
                    <ConnectionSkeleton />
                ) : connections.length > 0 ? (
                    <>
                        <div className="sync-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {connections.slice((profilePage - 1) * itemsPerPage, profilePage * itemsPerPage).map((inv, idx) => {
                                const name = getDisplayName(inv);
                                const persona = getConnectedPersona(inv);
                                return (
                                    <div
                                        key={inv.id || idx}
                                        onClick={() => setSelectedConnection(inv)}
                                        style={{ 
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                                            padding: '12px 16px', background: 'rgba(255,255,255,0.03)', 
                                            borderRadius: '12px', cursor: 'pointer',
                                            transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                                        }}
                                    >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0, paddingRight: '12px' }}>
                                                {/* Glassmorphism Avatar */}
                                                <div style={{
                                                    width: '36px', height: '36px', borderRadius: '50%',
                                                    background: inv._connected_image_url ? 'transparent' : 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '14px', fontWeight: '700', color: 'white', flexShrink: 0,
                                                    boxShadow: '0 0 0 2px rgba(255,255,255,0.03)',
                                                    overflow: 'hidden'
                                                }}>
                                                    {inv._connected_image_url ? (
                                                        <img src={inv._connected_image_url} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        getAvatar(inv)
                                                    )}
                                                </div>
                                                
                                                {/* Text Container */}
                                                <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 }}>
                                                    <div style={{ 
                                                        fontSize: '14px', fontWeight: '600', color: 'white',
                                                        wordBreak: 'break-word', overflowWrap: 'break-word'
                                                    }}>
                                                        {name}
                                                    </div>
                                                    <div style={{ 
                                                        fontSize: '11px', color: 'rgba(255,255,255,0.6)', 
                                                        display: 'flex', alignItems: 'center', gap: '6px',
                                                        textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600'
                                                    }}>
                                                        {persona ? (
                                                            <>
                                                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ec4899', display: 'inline-block', flexShrink: 0, boxShadow: '0 0 8px #ec4899' }}></span>
                                                                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{persona}</span>
                                                            </>
                                                        ) : (
                                                            <span>🔗 Linked</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (onCompareRequested) onCompareRequested(inv);
                                                    }}
                                                    style={{ 
                                                        fontSize: '11px', padding: '4px 10px', borderRadius: '16px', 
                                                        background: 'rgba(236,72,153,0.15)', 
                                                        color: '#f472b6', 
                                                        border: '1px solid rgba(236,72,153,0.3)', 
                                                        cursor: 'pointer',
                                                        fontWeight: '600',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.background = 'rgba(236,72,153,0.25)';
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.background = 'rgba(236,72,153,0.15)';
                                                    }}
                                                >
                                                    Compare
                                                </button>
                                                <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '16px', display: 'flex', alignItems: 'center' }}>›</span>
                                            </div>
                                    </div>
                                );
                            })}
                        </div>
                        <PaginationControls 
                            currentPage={profilePage} 
                            totalItems={connections.length} 
                            onPageChange={setProfilePage} 
                        />
                    </>
                ) : (
                    <div style={{ textAlign: 'center', padding: '30px 20px', color: 'rgba(255,255,255,0.35)' }}>
                        <p style={{ fontSize: '13px', margin: 0 }}>No connections yet.</p>
                        <p style={{ fontSize: '11px', marginTop: '6px', color: 'rgba(255,255,255,0.25)', lineHeight: '1.4' }}>
                            Connections will appear here once you join a friend's link<br/>
                            or someone joins yours.
                        </p>
                    </div>
                )}
            </div>

            {selectedConnection && (() => {
                const inv = selectedConnection;
                const name = getDisplayName(inv);
                const persona = getConnectedPersona(inv);

                const raw = inv._perspective === 'received' ? inv.inviter_test_results : inv.invited_test_results;
                let results = null;
                try { results = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch {}

                const baseSource = results || {};
                const pCard = results?.cards_data?.personality_card || results?.personality_card || {};
                const leftPct = pCard?.brainBalance?.leftPercent || baseSource?.left_percent || baseSource?.left_percentage || 50;
                const rightPct = pCard?.brainBalance?.rightPercent || baseSource?.right_percent || baseSource?.right_percentage || 50;
                const leftLabel = 'Logic';
                const rightLabel = 'Creativity';
                
                const derivedQuote = leftPct > 55 ? '"The analytical mind searching for hidden precision."' : (rightPct > 55 ? '"The empathetic soul with a creator\'s drive."' : '"The balanced orchestrator of logic and intuition."');
                const rawQuote = pCard?.oneLiner || derivedQuote;
                const quote = typeof rawQuote === 'string' ? rawQuote : (rawQuote?.text || JSON.stringify(rawQuote));

                const traits = Array.isArray(pCard?.keyInsights) && pCard.keyInsights.length > 0 
                  ? pCard.keyInsights.map(t => typeof t === 'object' ? (t.insight || t.label || t.name || JSON.stringify(t)) : String(t))
                  : (baseSource?.insights?.top_traits || baseSource?.trait_analysis?.top_traits || ['Balanced Ambivert', 'Intuitive']);
                  
                const getStrengthsLabels = () => {
                    if (Array.isArray(pCard?.coreStrengths) && pCard.coreStrengths.length > 0) {
                        return pCard.coreStrengths.map(s => typeof s === 'object' ? (s.label || s.strength || s.name || JSON.stringify(s)) : String(s));
                    }
                    return baseSource?.insights?.core_strengths || ['Empathetic Connection', 'Steady Resolve', 'Optimistic Vision'];
                };
                const strengths = getStrengthsLabels();

                return (
                    <div style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
                    }} onClick={() => setSelectedConnection(null)}>
                        <div style={{
                            background: 'linear-gradient(145deg, #1a0a2e, #0f1020)',
                            border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px',
                            padding: '28px 24px', width: '100%', maxWidth: '420px',
                            maxHeight: '85vh', overflowY: 'auto', position: 'relative'
                        }} onClick={e => e.stopPropagation()}>
                            <button onClick={() => setSelectedConnection(null)} style={{
                                position: 'absolute', top: '16px', right: '16px',
                                background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%',
                                width: '30px', height: '30px', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: '16px'
                            }}>✕</button>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                                <div style={{
                                    width: '52px', height: '52px', borderRadius: '50%',
                                    background: inv._connected_image_url ? 'transparent' : 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: '22px', fontWeight: '700', color: 'white', flexShrink: 0,
                                    overflow: 'hidden'
                                }}>
                                    {inv._connected_image_url ? (
                                        <img src={inv._connected_image_url} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        getAvatar(inv)
                                    )}
                                </div>
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '18px' }}>{name}</div>
                                    {persona && <div style={{ fontSize: '12px', color: '#ec4899', fontWeight: '600', marginTop: '2px' }}>{persona}</div>}
                                </div>
                            </div>

                            {results ? (
                                <>
                                    {quote && (
                                        <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', fontStyle: 'italic', margin: '0 0 20px', lineHeight: '1.5', borderLeft: '3px solid rgba(236,72,153,0.4)', paddingLeft: '12px' }}>
                                            "{typeof quote === 'string' ? quote.replace(/^"|"$/g, '') : ''}"
                                        </p>
                                    )}

                                    <div style={{ marginBottom: '20px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#888', marginBottom: '6px', textTransform: 'uppercase' }}>
                                            <span style={{ color: '#3b82f6' }}>{leftLabel} {leftPct}%</span>
                                            <span style={{ color: '#ec4899' }}>{rightPct}% {rightLabel}</span>
                                        </div>
                                        <div style={{ height: '6px', borderRadius: '3px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden', display: 'flex' }}>
                                            <div style={{ width: `${leftPct}%`, background: 'linear-gradient(90deg,#3b82f6,#8b5cf6)', borderRadius: '3px 0 0 3px' }} />
                                            <div style={{ width: `${rightPct}%`, background: 'linear-gradient(90deg,#8b5cf6,#ec4899)', borderRadius: '0 3px 3px 0' }} />
                                        </div>
                                    </div>

                                    {traits.length > 0 && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '600' }}>Key Traits</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                {traits.slice(0, 5).map((t, i) => (
                                                    <span key={i} style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '20px', padding: '4px 10px', fontSize: '11px', color: '#c4b5fd' }}>{t}</span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {strengths.length > 0 && (
                                        <div>
                                            <div style={{ fontSize: '10px', color: '#888', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '600' }}>Top Strengths</div>
                                            {strengths.slice(0, 4).map((s, i) => (
                                                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <span style={{ fontSize: '13px' }}>{s}</span>
                                                    <span style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '10px', background: i === 0 ? 'rgba(52,211,153,0.2)' : 'rgba(59,130,246,0.15)', color: i === 0 ? '#34d399' : '#60a5fa', fontWeight: '600' }}>
                                                        {i === 0 ? 'Exceptional' : 'Strong'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (onCompareRequested) onCompareRequested(inv);
                                            }}
                                            style={{
                                                width: '100%',
                                                background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                                                border: 'none',
                                                borderRadius: '12px', padding: '12px', color: 'white',
                                                fontSize: '13px', fontWeight: '700', cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                                boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)'
                                            }}
                                        >
                                            ✨ Evaluate Compatibility
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '13px', padding: '24px 0' }}>
                                    This user hasn't completed their profile yet.
                                </p>
                            )}
                        </div>
                    </div>
                );
            })()}



            <div className="glass-panel" style={{ padding: '24px', marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 className="section-title" style={{ margin: 0, textTransform: 'uppercase' }}>Leaderboard</h3>
                    <select 
                        value={selectedCategory} 
                        onChange={e => setSelectedCategory(e.target.value)}
                        style={{
                            background: 'rgba(0,0,0,0.3)', color: 'white', border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '8px', padding: '6px 12px', fontSize: '12px', outline: 'none', cursor: 'pointer'
                        }}
                    >
                        <option value="Overall">Overall Compatibility</option>
                        <option value="work">Professional Synergy</option>
                        <option value="friendship">Friendship Resonance</option>
                        <option value="relationship">Romantic Harmony</option>
                        <option value="cofounder">Co-Founder Alignment</option>
                    </select>
                </div>

                {isInitialLoading ? (
                    <ConnectionSkeleton />
                ) : (() => {
                    const getThemeLabel = (themeId) => {
                        const themeMap = {
                            work: '(Professional)',
                            friendship: '(Friendship)',
                            relationship: '(Romantic)',
                            cofounder: '(Co-Founder)'
                        };
                        return themeMap[themeId] || '';
                    };

                    let rankedConnections = [];
                    connections.forEach(inv => {
                        const name = getDisplayName(inv);
                        const avatar = getAvatar(inv);
                        const reports = compatibilityReports.filter(r => r.connection_id === inv.id);
                        
                        if (selectedCategory === 'Overall') {
                            reports.forEach(r => {
                                if (r.theme && r.score !== undefined && r.score !== null) {
                                    rankedConnections.push({
                                        id: `${inv.id}-${r.theme}`,
                                        inv,
                                        avatar,
                                        displayName: `${name} ${getThemeLabel(r.theme)}`,
                                        score: r.score,
                                        theme: r.theme
                                    });
                                }
                            });
                        } else {
                            const r = reports.find(r => r.theme === selectedCategory);
                            if (r && r.score !== undefined && r.score !== null) {
                                rankedConnections.push({
                                    id: `${inv.id}-${r.theme}`,
                                    inv,
                                    avatar,
                                    displayName: name,
                                    score: r.score,
                                    theme: r.theme
                                });
                            }
                        }
                    });

                    if (rankedConnections.length > 0) {
                        const sorted = rankedConnections.sort((a,b) => b.score - a.score);
                        const paginated = sorted.slice((leaderboardPage - 1) * itemsPerPage, leaderboardPage * itemsPerPage);

                        return (
                            <>
                                <div className="sync-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {paginated.map((person, idx) => {
                                        const globalIdx = (leaderboardPage - 1) * itemsPerPage + idx;
                                        return (
                                            <div 
                                                key={person.id || person.inv.id || idx} 
                                                onClick={() => onCompareRequested(person.inv, person.theme)}
                                                style={{ 
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                                                    padding: '12px 16px', background: 'rgba(255,255,255,0.03)', 
                                                    borderRadius: '12px', cursor: 'pointer',
                                                    transition: 'background 0.2s'
                                                }}
                                                onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                                                onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div style={{ width: '24px', textAlign: 'center', fontWeight: 'bold', color: globalIdx === 0 ? '#fbbf24' : globalIdx === 1 ? '#94a3b8' : globalIdx === 2 ? '#b45309' : 'rgba(255,255,255,0.3)' }}>
                                                        #{globalIdx + 1}
                                                    </div>
                                                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #8b5cf6, #ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '14px', color: 'white' }}>
                                                        {person.avatar}
                                                    </div>
                                                    <div style={{ fontSize: '14px', fontWeight: '600', color: 'white' }}>{person.displayName}</div>
                                                </div>
                                                <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#34d399' }}>
                                                    {person.score}%
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <PaginationControls 
                                    currentPage={leaderboardPage} 
                                    totalItems={rankedConnections.length} 
                                    onPageChange={setLeaderboardPage} 
                                />
                            </>
                        );
                    }

                    return (
                        <div style={{ textAlign: 'center', padding: '30px 20px', color: 'rgba(255,255,255,0.35)' }}>
                            <p style={{ fontSize: '13px', margin: 0 }}>No ranked connections yet.</p>
                            <p style={{ fontSize: '11px', marginTop: '6px', color: 'rgba(255,255,255,0.25)' }}>
                                Explore compatibility topics with connected profiles to see scores!
                            </p>
                        </div>
                    );
                })()}
            </div>

            {/* ── Invite & Share Section ── */}
            <div className="invite-cta-section">
                <div className="invite-cta-header">
                    <span className="invite-cta-emoji">🔮</span>
                    <h3 className="invite-cta-title">See How You Match With Anyone</h3>
                    <p className="invite-cta-subtitle">
                        Send your link. When they take the test, you'll unlock compatibility insights instantly.
                    </p>
                </div>

                {/* Preview grid */}
                <div className="invite-cta-preview-grid">
                    <div className="invite-cta-preview-item">
                        <span>💯</span><span>Compatibility Score</span>
                    </div>
                    <div className="invite-cta-preview-item">
                        <span>🤝</span><span>How You Click</span>
                    </div>
                    <div className="invite-cta-preview-item">
                        <span>⚡</span><span>Friction Points</span>
                    </div>
                    <div className="invite-cta-preview-item">
                        <span>💡</span><span>Tips to Get Along</span>
                    </div>
                </div>

                {/* Share actions */}
                {!inviteLink ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0', color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>
                        {isLoading ? 'Generating your unique link...' : 'Loading...'}
                    </div>
                ) : (
                    <SharePanel link={inviteLink} />
                )}
            </div>

            <button className="replay-button" style={{ marginBottom: '20px' }} onClick={onRestart}>
                Redo Your Analysis
            </button>
        </div>
    );
};

export default SyncTab;
